// 3D модель скиммерного бассейна (чаша: дно + 4 стены).
import React, { useEffect, useMemo, useState, useRef } from 'react';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { applyWorldUVs } from '../utils/uvHelpers';
import { getTextureUrlFromFinishing } from '../hooks/usePoolTexture';
import { usePoolFinishingTexture } from '../hooks/usePoolFinishingTexture';
import { PROC_POOL_GEOMETRY } from '../config/constants';
import { useOptimizedTexture } from '../../../hooks/useOptimizedTexture';

const {
  SCALE_XZ,
  BASE_CONCRETE_THICKNESS,
  WALL_HEIGHT_FACTOR,
  PLASTER_THICKNESS,
  BOTTOM_CONCRETE_THICKNESS,
  SKIMMER_BEVEL_SIZE: FIXED_BEVEL_SIZE,
  POOL_GROUP_Y_OFFSET,
} = PROC_POOL_GEOMETRY;
const DEFAULT_PLASTER_TEXTURE_SCALE_BASE = 2.0;
const CUSTOM_FINISHING_TEXTURE_SCALE = 0.3;
const REFERENCE_POOL_AREA = 12;
const MIN_DEFAULT_PLASTER_TEXTURE_SCALE = 0.8;
const MAX_DEFAULT_PLASTER_TEXTURE_SCALE = 4.0;
const DEFAULT_CONCRETE_TEXTURE_SCALE_BASE = 0.5;
const MIN_CONCRETE_TEXTURE_SCALE = 0.22;
const MAX_CONCRETE_TEXTURE_SCALE = 0.6;

function createRectPath(halfX, halfZ) {
  const path = new THREE.Path();
  path.moveTo(-halfX, -halfZ);
  path.lineTo(-halfX, halfZ);
  path.lineTo(halfX, halfZ);
  path.lineTo(halfX, -halfZ);
  path.lineTo(-halfX, -halfZ);
  return path;
}

function createRectFrameWithHole(outerHalfX, outerHalfZ, innerHalfX, innerHalfZ) {
  const frame = new THREE.Shape();
  frame.moveTo(-outerHalfX, -outerHalfZ);
  frame.lineTo(outerHalfX, -outerHalfZ);
  frame.lineTo(outerHalfX, outerHalfZ);
  frame.lineTo(-outerHalfX, outerHalfZ);
  frame.lineTo(-outerHalfX, -outerHalfZ);
  frame.holes.push(createRectPath(innerHalfX, innerHalfZ));
  return frame;
}

function removeExtrudeBottomCapTriangles(geometry) {
  const EPS = 1e-5;
  const pos = geometry.attributes.position;
  const index = geometry.index;
  if (!index) return geometry;
  const total = index.count;
  // Предварительно выделяем максимальный буфер — избегаем реаллокаций через push
  const buf = new Int32Array(total);
  let len = 0;
  for (let i = 0; i < total; i += 3) {
    const a = index.getX(i);
    const b = index.getX(i + 1);
    const c = index.getX(i + 2);
    if (Math.abs(pos.getZ(a)) < EPS && Math.abs(pos.getZ(b)) < EPS && Math.abs(pos.getZ(c)) < EPS) continue;
    buf[len++] = a;
    buf[len++] = b;
    buf[len++] = c;
  }
  const result = geometry.clone();
  result.setIndex(Array.from(buf.subarray(0, len)));
  result.computeVertexNormals();
  return result;
}

// Чистая функция вне компонента — вызывается и синхронно (первый рендер),
// и из useEffect (обновления после paint, не блокируют кадр).
function buildPoolGeometries(L, W, D, borderType, plasterTextureScale, adaptiveConcreteTextureScale) {
  const LVis = L * SCALE_XZ;
  const WVis = W * SCALE_XZ;
  const wallHeightLocal = D * WALL_HEIGHT_FACTOR;

  let thicknessLocal;
  if (borderType === 'wide') {
    thicknessLocal = 0.2;
  } else if (borderType === 'narrow') {
    thicknessLocal = 0.07;
  } else {
    thicknessLocal = 0.14;
  }

  const bottomThickness = BOTTOM_CONCRETE_THICKNESS;
  const baseThickness = BASE_CONCRETE_THICKNESS;
  const innerX = WVis / 2 - baseThickness;
  const innerZ = LVis / 2 - baseThickness;
  const skimmerBevelSize = FIXED_BEVEL_SIZE;
  const plasterThickness = PLASTER_THICKNESS;
  const plasterOuterX = innerX + plasterThickness;
  const plasterOuterZ = innerZ + plasterThickness;
  const baseOuterX = innerX + thicknessLocal + skimmerBevelSize;
  const baseOuterZ = innerZ + thicknessLocal + skimmerBevelSize;

  const plasterFrame = createRectFrameWithHole(plasterOuterX, plasterOuterZ, innerX, innerZ);
  const innerWallDepth = wallHeightLocal;
  const innerWallYShift = 0;

  let plasterWallsGeom = new THREE.ExtrudeGeometry(plasterFrame, {
    depth: innerWallDepth,
    bevelEnabled: false,
  });

  const innerConcreteThickness = thicknessLocal - skimmerBevelSize;
  const innerConcreteOuterX = innerX + innerConcreteThickness;
  const innerConcreteOuterZ = innerZ + innerConcreteThickness;

  const innerShellFrame = createRectFrameWithHole(
    innerConcreteOuterX, innerConcreteOuterZ, plasterOuterX, plasterOuterZ
  );
  let innerWallsGeom = new THREE.ExtrudeGeometry(innerShellFrame, {
    depth: innerWallDepth,
    bevelEnabled: false,
  });
  innerWallsGeom = removeExtrudeBottomCapTriangles(innerWallsGeom);

  const outerShellFrame = createRectFrameWithHole(
    baseOuterX, baseOuterZ, innerConcreteOuterX, innerConcreteOuterZ
  );
  let outerWallsGeom = new THREE.ExtrudeGeometry(outerShellFrame, {
    depth: wallHeightLocal,
    bevelEnabled: true,
    bevelSegments: 1,
    bevelSteps: 1,
    bevelSize: skimmerBevelSize,
    bevelThickness: skimmerBevelSize,
  });

  const slabDepth = bottomThickness + skimmerBevelSize;
  const bottomFrameShape = createRectFrameWithHole(
    baseOuterX, baseOuterZ, innerConcreteOuterX, innerConcreteOuterZ
  );
  let bottomGeom = new THREE.ExtrudeGeometry(bottomFrameShape, {
    depth: slabDepth,
    bevelEnabled: false,
  });
  bottomGeom = removeExtrudeBottomCapTriangles(bottomGeom);
  bottomGeom.rotateX(-Math.PI / 2);
  bottomGeom.computeBoundingBox();
  bottomGeom.translate(0, -skimmerBevelSize - bottomGeom.boundingBox.min.y, 0);
  bottomGeom.computeBoundingBox();

  let concreteBottomUnderfaceGeom = new THREE.PlaneGeometry(baseOuterX * 2, baseOuterZ * 2, 1, 1);
  concreteBottomUnderfaceGeom.rotateX(Math.PI / 2);
  concreteBottomUnderfaceGeom.translate(0, bottomGeom.boundingBox.min.y - 0.00005, 0);

  const holeLinerY = (bottomThickness - skimmerBevelSize) / 2;
  const holeEps = 0.001;
  const px = new THREE.PlaneGeometry(2 * innerConcreteOuterZ, slabDepth);
  px.rotateY(-Math.PI / 2);
  px.translate(innerConcreteOuterX - holeEps, holeLinerY, 0);
  const nx = new THREE.PlaneGeometry(2 * innerConcreteOuterZ, slabDepth);
  nx.rotateY(Math.PI / 2);
  nx.translate(-innerConcreteOuterX + holeEps, holeLinerY, 0);
  const pz = new THREE.PlaneGeometry(2 * innerConcreteOuterX, slabDepth);
  pz.translate(0, holeLinerY, innerConcreteOuterZ - holeEps);
  const nz = new THREE.PlaneGeometry(2 * innerConcreteOuterX, slabDepth);
  nz.rotateY(Math.PI);
  nz.translate(0, holeLinerY, -innerConcreteOuterZ + holeEps);
  let plasterHoleLinerGeom = mergeGeometries([px, nx, pz, nz]);
  [px, nx, pz, nz].forEach((g) => g.dispose());

  let plasterBottomGeom = new THREE.BoxGeometry(
    innerConcreteOuterX * 2, PLASTER_THICKNESS, innerConcreteOuterZ * 2
  );

  innerWallsGeom = applyWorldUVs(innerWallsGeom, adaptiveConcreteTextureScale);
  outerWallsGeom = applyWorldUVs(outerWallsGeom, adaptiveConcreteTextureScale);
  plasterWallsGeom = applyWorldUVs(plasterWallsGeom, plasterTextureScale);
  bottomGeom = applyWorldUVs(bottomGeom, adaptiveConcreteTextureScale);
  concreteBottomUnderfaceGeom = applyWorldUVs(concreteBottomUnderfaceGeom, adaptiveConcreteTextureScale);
  plasterHoleLinerGeom = applyWorldUVs(plasterHoleLinerGeom, plasterTextureScale);
  plasterBottomGeom = applyWorldUVs(plasterBottomGeom, plasterTextureScale);

  return {
    innerConcreteWallGeometry: innerWallsGeom,
    outerConcreteWallGeometry: outerWallsGeom,
    plasterWallGeometry: plasterWallsGeom,
    concreteBottomGeometry: bottomGeom,
    concreteBottomUnderfaceGeometry: concreteBottomUnderfaceGeom,
    plasterHoleLinerGeometry: plasterHoleLinerGeom,
    plasterBottomGeometry: plasterBottomGeom,
    bottomThickness,
    skimmerBevelSize,
    innerWallYShift,
  };
}

function disposeGeoSet(geoSet) {
  if (!geoSet) return;
  geoSet.innerConcreteWallGeometry?.dispose();
  geoSet.outerConcreteWallGeometry?.dispose();
  geoSet.plasterWallGeometry?.dispose();
  geoSet.concreteBottomGeometry?.dispose();
  geoSet.concreteBottomUnderfaceGeometry?.dispose();
  geoSet.plasterHoleLinerGeometry?.dispose();
  geoSet.plasterBottomGeometry?.dispose();
}

/**
 * @param {Object} props
 * @param {number} props.length
 * @param {number} props.width
 * @param {number} props.depth
 * @param {'normal' | 'wide' | 'narrow'} [props.borderType]
 */
export function SimpleSquarePoolModel({
  length,
  width,
  depth,
  borderType = 'normal',
  selectedFinishing = null
}) {
  const L = Math.max(0.1, Number(length) || 4);
  const W = Math.max(0.1, Number(width) || 4);
  const D = Math.max(0.1, Number(depth) || 1.5);

  const adaptiveDefaultPlasterTextureScale = useMemo(() => {
    const area = Math.max(0.1, L * W);
    const ratio = area / REFERENCE_POOL_AREA;
    const scaled = DEFAULT_PLASTER_TEXTURE_SCALE_BASE * Math.sqrt(ratio);
    return Math.max(MIN_DEFAULT_PLASTER_TEXTURE_SCALE, Math.min(MAX_DEFAULT_PLASTER_TEXTURE_SCALE, scaled));
  }, [L, W]);

  const adaptiveConcreteTextureScale = useMemo(() => {
    const area = Math.max(0.1, L * W);
    const ratio = area / REFERENCE_POOL_AREA;
    const scaled = DEFAULT_CONCRETE_TEXTURE_SCALE_BASE * Math.sqrt(ratio);
    return Math.max(MIN_CONCRETE_TEXTURE_SCALE, Math.min(MAX_CONCRETE_TEXTURE_SCALE, scaled));
  }, [L, W]);

  const finishingUrl = getTextureUrlFromFinishing(selectedFinishing) || '/models/pools/plaster.webp';
  const plasterTextureScale = selectedFinishing ? CUSTOM_FINISHING_TEXTURE_SCALE : adaptiveDefaultPlasterTextureScale;

  const baseTexture = useOptimizedTexture('/models/pools/beton.webp');
  const plasterTexture = usePoolFinishingTexture(finishingUrl);
  useMemo(() => {
    baseTexture.wrapS = THREE.RepeatWrapping;
    baseTexture.wrapT = THREE.RepeatWrapping;
    plasterTexture.wrapS = THREE.RepeatWrapping;
    plasterTexture.wrapT = THREE.RepeatWrapping;
    baseTexture.needsUpdate = true;
    plasterTexture.needsUpdate = true;
    return null;
  }, [baseTexture, plasterTexture]);

  // MeshLambertMaterial вместо MeshStandardMaterial: при roughness 0.8-0.9 (матовый
  // бетон/штукатурка) PBR-специфичный вклад (roughness/metalness) визуально
  // ничтожен, а Lambert заметно дешевле на пересчёт под каждый источник света —
  // тот же приём, что уже применён у решётки/прожекторов (см. grateMaterial.js).
  const concreteMaterial = useMemo(
    () => new THREE.MeshLambertMaterial({
      map: baseTexture,
      color: 0xf2f2f2,
      emissive: new THREE.Color(0.035, 0.035, 0.035)
    }),
    [baseTexture]
  );

  const plasterMaterial = useMemo(
    () => new THREE.MeshLambertMaterial({
      map: plasterTexture,
      color: 0xffffff,
      side: THREE.FrontSide
    }),
    [plasterTexture]
  );

  // Геометрия: первый рендер — синхронно, все следующие — после paint (не блокируют кадр)
  const [geoSet, setGeoSet] = useState(() =>
    buildPoolGeometries(L, W, D, borderType, plasterTextureScale, adaptiveConcreteTextureScale)
  );
  const isMounted = useRef(false);

  useEffect(() => {
    // Пропускаем первый вызов — геометрия уже вычислена в useState
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }
    // rAF откладывает вычисление до следующего кадра и позволяет отменить,
    // если размеры снова изменятся до завершения (cleanup отменит rAF)
    let active = true;
    const raf = requestAnimationFrame(() => {
      if (!active) return;
      const newGeo = buildPoolGeometries(L, W, D, borderType, plasterTextureScale, adaptiveConcreteTextureScale);
      if (!active) { disposeGeoSet(newGeo); return; }
      setGeoSet(prev => {
        setTimeout(() => disposeGeoSet(prev), 150);
        return newGeo;
      });
    });
    return () => {
      active = false;
      cancelAnimationFrame(raf);
    };
  }, [L, W, D, borderType, plasterTextureScale, adaptiveConcreteTextureScale]);

  // Очистка при размонтировании
  useEffect(() => {
    return () => {
      concreteMaterial.dispose();
      plasterMaterial.dispose();
      disposeGeoSet(geoSet);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    innerConcreteWallGeometry,
    outerConcreteWallGeometry,
    plasterWallGeometry,
    concreteBottomGeometry,
    concreteBottomUnderfaceGeometry,
    plasterHoleLinerGeometry,
    plasterBottomGeometry,
    bottomThickness,
    skimmerBevelSize,
    innerWallYShift,
  } = geoSet;

  return (
    <group position={[0, POOL_GROUP_Y_OFFSET, 0]}>
      <mesh
        geometry={plasterWallGeometry}
        material={plasterMaterial}
        position={[0, innerWallYShift, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      />

      <mesh
        geometry={innerConcreteWallGeometry}
        material={concreteMaterial}
        position={[0, innerWallYShift, 0]}
        rotation={[-Math.PI / 2, 0, 0]}
      />

      <mesh
        geometry={outerConcreteWallGeometry}
        material={concreteMaterial}
        rotation={[-Math.PI / 2, 0, 0]}
      />

      <mesh geometry={concreteBottomGeometry} material={concreteMaterial} />

      <mesh geometry={concreteBottomUnderfaceGeometry} material={concreteMaterial} />

      <mesh geometry={plasterHoleLinerGeometry} material={plasterMaterial} />

      <mesh
        geometry={plasterBottomGeometry}
        material={plasterMaterial}
        position={[0, bottomThickness + PLASTER_THICKNESS / 2, 0]}
      />
    </group>
  );
}